import { useParams } from 'react-router-dom';
import { FormikProvider, useFormik } from 'formik';
import { LegalEntityModel, PersonModel } from 'app/shared/oib/oib.model';
import { fileInsight, PARTY_TYPE_ENUM, partyTypes, SUBJECT_TYPE_ENUM } from 'app/shared/enumeration/enums';
import { Button, Input, Modal, Toggle } from 'edozvola-frontend-lib/dist/ui/components';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Loader } from 'app/shared/loader/loader';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import OibInput from 'app/shared/oib/oib-input';
import AddressInput from 'app/shared/address-input/address-input';
import React from 'react';
import { fetchPartiesForAssigneeQuery, fetchPartyQuery, savePartyQuery } from 'app/edozvola/cases/parties/service/party.queries';
import { partyValidationSchema } from 'app/edozvola/cases/parties/model/party.schema';
import { partyInitialData } from 'app/edozvola/cases/parties/model/party.model';
import { fetchPartyFeatureQuery, fetchProcurationTypesQuery, fetchSubjectTypeQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { formatAddress } from 'app/shared/address-input/address-helper';
import {ToggleWithLabels} from "edozvola-frontend-lib/dist/ui/components/toggle-with-labels";

export interface IPartyFormProps {
  partyId: string | null;
  setPartyId: any;
  readOnly: boolean;
}

const PartyForm = (props: IPartyFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();
  const { data: procurationTypeOptions, isLoading: procurationTypeOptionsLoading } = fetchProcurationTypesQuery();
  const { data: partyFeatureOptions, isLoading: partyFeatureOptionsLoading } = fetchPartyFeatureQuery();
  const { data: partyData, isLoading: partyDataLoading } = fetchPartyQuery(props.partyId);
  const { data: subjectTypeOptions, isLoading: subjectTypeOptionsLoading } = fetchSubjectTypeQuery();

  const formik = useFormik({
    initialValues: partyData || partyInitialData,
    onSubmit: values => savePartyMutation(values),
    validationSchema: partyValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    initialTouched: {
      person: {
        oib: !partyData,
      },
      legalEntity: {
        oib: !partyData,
      },
    },
  });

  const filteredSubjectTypeOptions = React.useMemo(() => {
    if (!formik.values.partyType || formik.values.partyType[0].id === PARTY_TYPE_ENUM.STRANKA) {
      return subjectTypeOptions;
    }

    // Ako je opunomoćenik => isključi obrt (3) i OPG (5)
    if (formik.values.partyType[0].id === PARTY_TYPE_ENUM.OPUNOMOCENIK_STRANKE) {
      return subjectTypeOptions?.filter((opt: any) => opt.id !== SUBJECT_TYPE_ENUM.OBRT && opt.id !== SUBJECT_TYPE_ENUM.OPG);
    }

    return subjectTypeOptions;
  }, [formik.values.partyType, subjectTypeOptions]);

  const isOsobaSelectedEqual = (subjectType: SUBJECT_TYPE_ENUM) =>
    formik.values.subjectType && formik.values.subjectType[0] && formik.values.subjectType[0].id === subjectType;

  const isPartySelectedEqual = (partyType: PARTY_TYPE_ENUM) =>
    formik.values.partyType && formik.values.partyType[0] && formik.values.partyType[0].id === partyType;

  const isPravnaIliFizickaOsobaSelected =
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA);

  const isObrtSlobodnaDjelatnostIliOpgSelected =
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.OBRT) ||
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.SLOBODNA_DJELATNOST) ||
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.OPG);

  const { data: partiesOptions, isLoading: partiesOptionsLoading } = fetchPartiesForAssigneeQuery(
    uuid,
    isPartySelectedEqual(PARTY_TYPE_ENUM.OPUNOMOCENIK_STRANKE),
    props.partyId
  );

  const { mutate: savePartyMutation, isPending: isPartySaving } = savePartyQuery(uuid, () => closeModal());

  const oibFetched = (fetchedOibData: PersonModel | LegalEntityModel) => {
    if (isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA)) {
      formik.setFieldValue('person', fetchedOibData);
    } else {
      formik.setFieldValue('legalEntity', fetchedOibData);
    }
  };

  const onOibChanged = (value: string) => {
    formik.setFieldValue('person.address', null);
    formik.setFieldValue('person.firstName', '');
    formik.setFieldValue('person.lastName', '');
    formik.setFieldValue('legalEntity.companyName', '');
    formik.setFieldValue('legalEntity.address', null);

    if (isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)) {
      formik.setTouched({
        ...formik.touched,
        legalEntity: {
          oib: true,
        },
      });
      formik.setFieldValue('legalEntity.oib', value);
    } else {
      formik.setTouched({
        ...formik.touched,
        person: {
          oib: true,
        },
      });
      formik.setFieldValue('person.oib', value);
    }
  };

  const onPartyTypeChange = (selected: any) => {
    formik.setFieldValue('partyType', selected);
    onOibChanged('');
    formik.setFieldValue('subjectType', []);

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  const onSubjectTypeChange = selected => {
    onOibChanged('');
    formik.setFieldValue('legalEntity.mb', '');
    formik.setFieldValue('subjectType', selected);

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  const closeModal = () => {
    props.setPartyId(null);
    formik.resetForm({ values: partyInitialData });
  };

  const phoneChanged = (event: any, field: string) => {
    formik.setFieldValue(field, event.target.value);
    event.preventDefault();
  };

  return (
    <>
      <Modal scrollable size={'xl'} isOpen={props.partyId !== null}>
        {props.readOnly ? (
          <ModalHeader toggle={() => closeModal()}>Pregled stranke/opunomoćenika</ModalHeader>
        ) : (
          <ModalHeader>{props.partyId === '' ? 'Unos stranke/opunomoćenika' : 'Uređivanje stranke/opunomoćenika'}</ModalHeader>
        )}

        <ModalBody>
          <FormikProvider value={formik}>
            {partyDataLoading && <Loader />}
            {!partyDataLoading && (
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta stranke'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu stranke'}
                      onItemSelect={selected => onPartyTypeChange(selected)}
                      selected={formik.values.partyType}
                      labelKey={'value'}
                      options={partyTypes}
                      disabled={props?.readOnly || props.partyId !== ''}
                      error={formik.errors.partyType as string}
                      showErrorMessage
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta subjekta'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu subjekta'}
                      onItemSelect={onSubjectTypeChange}
                      selected={formik.values.subjectType}
                      labelKey={'value'}
                      options={filteredSubjectTypeOptions}
                      disabled={props?.readOnly || props.partyId !== ''}
                      error={formik.errors.subjectType as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    {isPravnaIliFizickaOsobaSelected ? (
                      <OibInput
                        value={
                          isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? formik.values.legalEntity?.oib : formik.values.person?.oib
                        }
                        handleBlur={formik.handleBlur}
                        handleChange={event => onOibChanged(event.target.value)}
                        errors={isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? formik.errors.legalEntity : formik.errors.person}
                        touched={isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? formik.touched.legalEntity : formik.touched.person}
                        onOibFetched={oibFetched}
                        fetchLegalEntity={isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)}
                        disabled={
                          props.readOnly || !formik.values.subjectType || formik.values.subjectType.length === 0 || props.partyId !== ''
                        }
                      />
                    ) : (
                      isObrtSlobodnaDjelatnostIliOpgSelected && (
                        <>
                          <Label labelText="MB" />
                          <Input
                            value={formik.values.legalEntity?.mb}
                            id="mb"
                            name="mb"
                            maxLength={11}
                            disabled={props.readOnly || props.partyId !== ''}
                            onChange={event => formik.setFieldValue('legalEntity.mb', event.target.value)}
                            error={formik.errors.legalEntity && formik.errors.legalEntity['mb'] ? formik.errors.legalEntity['mb'] : ''}
                          />
                        </>
                      )
                    )}
                  </Col>
                </Row>
                {isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA) && (
                  <Row>
                    <Col md={6}>
                      <Label labelText={'Ime'} />
                      <Input
                        disabled
                        value={formik.values.person.firstName}
                        error={formik.errors.person && formik.errors.person['firstName'] ? formik.errors.person['firstName'] : ''}
                        maxLength={200}
                        onChange={event => formik.setFieldValue('person.firstName', event.target.value)}
                      />
                    </Col>
                    <Col md={6}>
                      <Label labelText={'Prezime'} />
                      <Input
                        disabled
                        value={formik.values.person.lastName}
                        error={formik.errors.person && formik.errors.person['lastName'] ? formik.errors.person['lastName'] : ''}
                        maxLength={200}
                        onChange={event => formik.setFieldValue('person.lastName', event.target.value)}
                      />
                    </Col>
                  </Row>
                )}
                {(isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) || isObrtSlobodnaDjelatnostIliOpgSelected) && (
                  <Row>
                    <Col md={6}>
                      <Label labelText={'Naziv'} />
                      <Input
                        disabled={props.readOnly || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)}
                        value={formik.values.legalEntity?.companyName}
                        error={
                          formik.errors.legalEntity && formik.errors.legalEntity['companyName']
                            ? formik.errors.legalEntity['companyName']
                            : ''
                        }
                        maxLength={200}
                        onChange={event => formik.setFieldValue('legalEntity.companyName', event.target.value)}
                      />
                    </Col>
                  </Row>
                )}
                {isPravnaIliFizickaOsobaSelected && formik.values.person?.address && (
                  <Row>
                    <Col md={6}>
                      <Label labelText="Adresa iz OIB-a" />
                      <span>{formatAddress(formik.values.person.address)}</span>
                    </Col>
                  </Row>
                )}

                {isPravnaIliFizickaOsobaSelected && formik.values.legalEntity?.address && (
                  <Row>
                    <Col md={6}>
                      <Label labelText="Adresa iz OIB-a" />
                      <span>{formatAddress(formik.values.legalEntity.address)}</span>
                    </Col>
                  </Row>
                )}

                <AddressInput
                  field={formik.values.partyAddress}
                  errors={formik.errors.partyAddress}
                  readOnly={props.readOnly}
                  setFieldValue={(field: string, value: any) => formik.setFieldValue(`partyAddress.${field}`, value)}
                />
                <Row>
                  <Col md={6}>
                    <Label labelText={'Email'} />
                    <Input
                      value={formik.values.email}
                      id={'email'}
                      name={'email'}
                      error={formik.errors.email ?? ''}
                      disabled={props.readOnly}
                      maxLength={60}
                      onChange={event => formik.setFieldValue(`email`, event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Telefon'} />
                    <Input
                      value={formik.values.telephoneNumber}
                      id={'telephoneNumber'}
                      name={'telephoneNumber'}
                      disabled={props.readOnly}
                      maxLength={50}
                      onChange={e => phoneChanged(e, 'telephoneNumber')}
                    />
                  </Col>
                </Row>
                {isPartySelectedEqual(PARTY_TYPE_ENUM.OPUNOMOCENIK_STRANKE) && (
                  <>
                    <Row>
                      <Col md={12}>
                        <Label labelText={'Stranka u postupku'} />
                        <PaginatedDropdown
                          placeholder="Odaberi stranku u postupku"
                          onItemSelect={selected => formik.setFieldValue('parties', selected)}
                          selected={formik.values.parties}
                          labelKey={'value'}
                          options={partiesOptionsLoading ? [] : partiesOptions}
                          multiple
                          disabled={props?.readOnly}
                          error={formik.errors?.parties as string}
                          showErrorMessage
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Label labelText={'Vrsta punomoći'} />
                        <PaginatedDropdown
                          placeholder={!props?.readOnly && 'Odaberi vrstu punomoći'}
                          onItemSelect={selected => formik.setFieldValue('procurationType', selected)}
                          selected={formik.values.procurationType}
                          labelKey={'value'}
                          options={procurationTypeOptionsLoading ? [] : procurationTypeOptions}
                          disabled={props?.readOnly}
                          error={formik.errors?.procurationType as string}
                          showErrorMessage
                        />
                      </Col>
                      <Col md={6}>
                        <Label labelText={'Punomoć aktivna'} />
                        <ToggleWithLabels
                          disabled={props.readOnly}
                          checked={formik.values.procurationStatus}
                          onChange={event => formik.setFieldValue('procurationStatus', event.target.checked)}
                        />
                      </Col>
                    </Row>
                  </>
                )}

                <hr />
                <div className={'form-header'}>Uvid u spis</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Uvid u spis'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi uvid u spis'}
                      onItemSelect={selected => formik.setFieldValue('fileInsight', selected)}
                      selected={formik.values.fileInsight}
                      labelKey={'value'}
                      options={fileInsight}
                      disabled={props?.readOnly}
                      error={formik.errors?.fileInsight as string}
                      showErrorMessage
                      positionFixed
                      clearButton
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Svojstvo stranke'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi svojstvo stranke'}
                      onItemSelect={selected => formik.setFieldValue('partyFeature', selected)}
                      selected={formik.values.partyFeature}
                      labelKey={'value'}
                      options={partyFeatureOptions}
                      disabled={props?.readOnly}
                      error={formik.errors?.partyFeature as string}
                      showErrorMessage
                      clearButton
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Katastarska čestica'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.cadastralParticle}
                      error={formik.errors.cadastralParticle ? `${formik.errors.cadastralParticle}` : ''}
                      onChange={event => formik.setFieldValue('cadastralParticle', event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Izjava po uvidu'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.insightStatement}
                      error={formik.errors.insightStatement ? `${formik.errors.insightStatement}` : ''}
                      onChange={event => formik.setFieldValue('insightStatement', event.target.value)}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </FormikProvider>
        </ModalBody>
        <ModalFooter>
          {!props.readOnly && (
            <>
              <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
              <Button
                label={'Spremi'}
                size={'lg'}
                disabled={!formik.dirty || !formik.isValid}
                isLoading={isPartySaving}
                color={'info'}
                onClick={() => formik.handleSubmit()}
              />
            </>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PartyForm;
